/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { WindowLocation } from '@reach/router';
import Helmet from 'react-helmet';

import config from '$/config';
import { useMe } from '$/hooks/useMe';
import useRunOnce from '$/hooks/useRunOnce';
import { AnalyticsProxy } from '$/analytics/AnalyticsProxy';
import { MeFragment } from './GraphQL/schema/types';
import { css } from '@emotion/react';
import { useHubspotTokenLazyQuery } from './GraphQL/schema/schema';

const CONSENT_COOKIES_EVENT_NAME = 'consentCookies';

interface HeapAPI {
  track: (event: string, properties?: Record<string, any>) => void;
  identify: (identity: string) => void;
  resetIdentity: () => void;
  addUserProperties: (properties: Record<string, any>) => void;
  addEventProperties: (properties: Record<string, any>) => void;
  removeEventProperty: (property: string) => void;
  clearEventProperties: () => void;
  appid: string;
  userId: string;
  identity: string | null;
  config: any;
}

declare global {
  interface Window {
    HubSpotConversations?: any;
    hsConversationsOnReady?: (() => void)[];
    hsConversationsSettings?: {
      loadImmediately?: boolean;
      identificationEmail?: string;
      identificationToken?: string;
    };
    heap?: HeapAPI;
    dataLayer?: any;
  }
}

export enum VisitorKind {
  LoggedOut = 'LoggedOut',
  Error = 'Error',
  Signup = 'Signup',
  User = 'User',
  Registrant = 'Registrant',
  SignupComplete = 'SignupComplete',
}

type SimpleFn = () => void;

interface Analytics {
  analytics?: AnalyticsProxy;
  hubspot?: {
    open: SimpleFn;
    close: SimpleFn;
    show: SimpleFn;
    hide: SimpleFn;
  };
}

export const BizAnalyticsContext = React.createContext<Analytics>({} as Analytics);

export const useBizAnalytics = (): Analytics => {
  return React.useContext(BizAnalyticsContext);
};

export const BizAnalytics: React.FC<{
  kind: VisitorKind;
  location?: WindowLocation;
  children: React.ReactNode;
}> = ({ kind, location, children }) => {
  const [context, setContext] = React.useState<Analytics>({} as Analytics);
  const [hubspotReady, setHubspotReady] = React.useState(false);
  const [cookieConsented, setCookieConsented] = React.useState(false);
  const me = useMe() as MeFragment | undefined;
  const [hubspotTokenQuery, hubspotTokenResult] = useHubspotTokenLazyQuery();

  const analytics = context.analytics;

  React.useEffect(() => {
    if (!analytics || !location?.pathname) {
      return;
    }

    analytics.page(location.pathname);
  }, [analytics, location?.pathname]);

  React.useEffect(() => {
    if (!analytics || !me?.id || !me?.team?.id || me?.impersonatingUser) {
      return;
    }

    analytics.identify(me);
  }, [analytics, me, me?.id, me?.impersonatingUser, me?.team.id]);

  React.useEffect(() => {
    if (!hubspotReady) return;

    const hubspot = {
      show: window.HubSpotConversations.widget.load,
      hide: window.HubSpotConversations.widget.remove,
      open: window.HubSpotConversations.widget.open,
      close: window.HubSpotConversations.widget.close,
    };

    setContext((prevContext) => ({
      ...prevContext,
      hubspot,
    }));
  }, [hubspotReady]);

  React.useEffect(() => {
    if (!me || !hubspotReady) return;
    hubspotTokenQuery();
  }, [me, hubspotReady, hubspotTokenQuery]);

  React.useEffect(() => {
    const consentCookies = (): void => {
      setCookieConsented(true);
    };

    window.addEventListener(CONSENT_COOKIES_EVENT_NAME, consentCookies);

    return (): void => {
      window.removeEventListener(CONSENT_COOKIES_EVENT_NAME, consentCookies);
    };
  }, []);

  useRunOnce(() => {
    window.hsConversationsSettings = {
      loadImmediately: false,
    };

    if (!window.HubSpotConversations) {
      const existingOnReady = window.hsConversationsOnReady ?? [];
      window.hsConversationsOnReady = [
        ...existingOnReady,
        (): void => {
          window.hsConversationsSettings = {
            loadImmediately: false,
          };

          setHubspotReady(true);
        },
      ];
    } else {
      setHubspotReady(true);
    }

    (function loadHubSpot(): void {
      const d = document;
      const s = d.createElement('script');

      s.src = `https://js.hs-scripts.com/${config.HUBSPOT_ACCT}.js`;
      s.async = true;
      d.getElementsByTagName('body')[0].appendChild(s);
    })();
  });

  React.useEffect(() => {
    if (kind === VisitorKind.Registrant || !me) return;

    const hubspotToken = hubspotTokenResult.data?.hubspotToken;
    let timeout: NodeJS.Timeout | undefined;
    if (
      kind === VisitorKind.Signup ||
      (me && !window.hsConversationsSettings?.identificationToken)
    ) {
      if (me) {
        // Only setup hubspot APIs once we have identified a user
        if (!hubspotToken) {
          if (config.isProduction) {
            console.error('No hubspotToken in me ', { me });
          }
        } else {
          window.hsConversationsSettings = {
            loadImmediately: false,
            identificationEmail: me.email,
            identificationToken: hubspotToken,
          };

          if (window.HubSpotConversations) {
            // We already loaded the hubspot bubble but without token so reload
            context.hubspot?.hide();
            timeout = setTimeout(() => context.hubspot?.show(), 100);
          }
        }
      }
    }
    return (): void => {
      timeout && clearTimeout(timeout);
    };
  }, [kind, me, hubspotTokenResult.data?.hubspotToken, context, hubspotReady]);

  React.useEffect(() => {
    if (context.analytics) return;
    if (!cookieConsented) return;

    const analytics = new AnalyticsProxy();

    setContext((prevContext) => {
      return { ...prevContext, analytics };
    });
  }, [context.analytics, cookieConsented]);

  return (
    <BizAnalyticsContext.Provider value={context}>
      {children}

      <Helmet>
        <script defer>
          {
            /*javascript*/ `
            var _hsq = window._hsq = window._hsq || [];

            _hsq.push(['addPrivacyConsentListener', function(consent) {
              /*
              if (!consent.allowed) {
                return;
              }
              */

              const cookieConsentEvent = new Event('${CONSENT_COOKIES_EVENT_NAME}');
              window.dispatchEvent(cookieConsentEvent);

              var email = '${me?.email ?? ''}';
              var id = '${me?.id ?? ''}';

              if (!window.heap) {
                window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
                window.heap.load("${config.HEAP_KEY}");
              }

              if (!window.eloadedGTM) {
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({"gtm.start":
                  new Date(Date.now()).getTime(),event:"gtm.js"});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!="dataLayer"?"&l="+l:"";j.async=true;j.src=
                  "https://www.googletagmanager.com/gtm.js?id="+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,"script","dataLayer","${config.GTM_KEY}");

                window.eloadedGTM = true;
              }

              if (email) {
                window.dataLayer.push({ event: 'start_pw', user_email: email });
              }
            }]);
            `
          }
        </script>
      </Helmet>

      {kind === VisitorKind.Signup && (
        <Helmet>
          {/* First promoter - Required to pay affiliates */}
          <script type='text/javascript'>
            {`(function(){var t=document.createElement("script");t.type="text/javascript",t.async=!0,t.src='https://cdn.firstpromoter.com/fprom.js',t.onload=t.onreadystatechange=function(){var t=this.readyState;if(!t||"complete"==t||"loaded"==t)try{$FPROM.init("d6mkk0ic", ".ewebinar.com")}catch(t){}};var e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(t,e)})();`}
          </script>
        </Helmet>
      )}

      {kind === VisitorKind.SignupComplete && config.isProduction && (
        <img
          src={`https://api.ewebinar.com/v1/conversion/pixel/1?tag=hs&email=${
            me?.email ?? ''
          }&amount=0&currency=USD&orderId=0`}
          width='1'
          height='1'
          css={css`
            display: none;
          `}
          alt='pixel'
        />
      )}
    </BizAnalyticsContext.Provider>
  );
};
